// A member's level set by hand (Adam's answer to question 2, 20 Oct; docs/accelerator-80-spec.md 4.9), owner and admin
// only, at 2-step login: the automatic level as the log counts it, the 4 levels to pick from and a note saying why
// (only owners and admins see it), saved with hub.set_accountability_level. The member sees the level alone, never the
// note or that it was set by hand. Back to automatic is a dialog that clears it (a null level). Nothing here draws
// unless the database has migration 80 (the staff log's level_set: Member.jsx), and the database refuses anyone else.
import { useId, useState } from 'react'
import { Button, Dialog, Field, Options, TextArea, useAdmin } from '../ui/index.js'
import { accountability } from '../../products/mastermind/log.js'
import { LEVEL_KEYS } from '../../products/mastermind/levels.js'
import { MM, PANEL } from './words.js'
import { setLevel } from './data.js'

const autoWord = log => MM.level[accountability(log).auto.key] || MM.level.getting_started

// In place under the head, as Change group opens: onDone(text) with the line to say once it's saved
export function LevelForm({ log, first, person, product, ctx, onDone, onCancel }) {
  const { api } = useAdmin()
  const set = log.level_set || null
  const [level, setPicked] = useState(set?.level || null)
  const [note, setNote] = useState(set?.note || '')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  const id = useId()
  const clean = note.trim()
  const unchanged = Boolean(set) && set.level === level && set.note === clean
  const ready = Boolean(level) && Boolean(clean) && !unchanged

  async function go() {
    if (busy || !ready) return
    setBusy(true); setProblem(null)
    try {
      await setLevel(api, { person, product, level, note: clean }, ctx)
      await onDone(MM.levelSaved(first, MM.level[level]))
    } catch (e) {
      setProblem(e.message)
      setBusy(false)
    }
  }

  return (
    <div className="amx-form amx-inline amx-level-form" role="group" aria-label={set ? MM.changeLevel : MM.setLevel}>
      <p className="amx-meta">{MM.levelAutoLine(autoWord(log))}</p>
      <div className="ad-field">
        <span className="ad-label ad-label--plain" id={`${id}-level`}>{MM.levelField(first)}</span>
        <Options row labelledBy={`${id}-level`} value={level} disabled={busy}
          options={LEVEL_KEYS.map(k => ({ value: k, label: MM.level[k] }))}
          onChange={v => { setPicked(v); setProblem(null) }} />
      </div>
      <Field label={MM.levelNoteField} plain hint={MM.levelNoteHint(first)} error={problem}>
        {props => <TextArea {...props} value={note} onChange={v => { setNote(v); setProblem(null) }} rows={3} maxLength={300} required />}
      </Field>
      <span className="amx-row-actions">
        <Button kind="ink" size="sm" busy={busy} disabled={!ready} onClick={go}>{MM.levelSave}</Button>
        <Button size="sm" disabled={busy} onClick={onCancel}>{PANEL.cancel}</Button>
      </span>
    </div>
  )
}

// Back to automatic: the level the log counts, and the note goes. onDone(text) once it's cleared
export function BackToAuto({ log, first, person, product, ctx, onDone, onClose }) {
  const { api } = useAdmin()
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  const auto = autoWord(log)
  async function go() {
    if (busy) return
    setBusy(true); setProblem(null)
    try {
      await setLevel(api, { person, product, level: null }, ctx)
      await onDone(MM.levelCleared(first, auto))
    } catch (e) {
      setProblem(e.message)
      setBusy(false)
    }
  }
  return (
    <Dialog title={MM.backToAutoTitle(first)} onClose={onClose} busy={busy}
      footer={<><Button kind="ink" busy={busy} onClick={go}>{MM.backToAutoYes}</Button><Button onClick={onClose} disabled={busy}>{MM.backToAutoNo}</Button></>}>
      <p className="amx-lede">{MM.backToAutoBody(first, auto)}</p>
      {problem && <p className="ad-error" role="alert">{problem}</p>}
    </Dialog>
  )
}
